import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Share2,
  TrendingUp,
  DollarSign,
  CheckCircle,
  ArrowRight,
  BarChart3,
  Zap,
  Gift,
  Loader2,
  Copy,
  Check,
  MousePointer,
  UserPlus,
  RefreshCw,
  Settings,
  CreditCard,
  Banknote,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';

const Affiliate = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState('');
  const [affiliateStats, setAffiliateStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  // Payout settings
  const [payoutSettings, setPayoutSettings] = useState({
    payoutMethod: null,
    paypalEmail: '',
    bankAccountNumber: '',
    bankIban: '',
    bankSwift: '',
    bankAccountHolder: ''
  });
  const [showPayoutForm, setShowPayoutForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Zkontrolovat affiliate status při načtení
  useEffect(() => {
    const checkAffiliateStatus = async () => {
      if (user) {
        try {
          const status = await api.getAffiliateStatus(user.id);
          if (status.isAffiliate) {
            setIsAffiliate(true);
            setAffiliateCode(status.code);
            // Načíst statistiky
            const stats = await api.getAffiliateStats(user.id);
            setAffiliateStats(stats);
            // Načíst payout settings
            const settings = await api.getPayoutSettings(user.id);
            setPayoutSettings({
              payoutMethod: settings.payoutMethod,
              paypalEmail: settings.paypalEmail || '',
              bankAccountNumber: settings.bankAccountNumber || '',
              bankIban: settings.bankIban || '',
              bankSwift: settings.bankSwift || '',
              bankAccountHolder: settings.bankAccountHolder || ''
            });
          }
        } catch (err) {
          console.error('Error checking affiliate status:', err);
        }
      }
      setCheckingStatus(false);
    };
    checkAffiliateStatus();
  }, [user]);

  const handleBecomeAffiliate = async () => {
    if (!user) {
      navigate('/login?redirect=/affiliate');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await api.setAffiliateStatus(user.id, true, 20);

      if (result.success) {
        setIsAffiliate(true);
        const codeData = await api.getReferralCode(user.id);
        setAffiliateCode(codeData.code);
        const stats = await api.getAffiliateStats(user.id);
        setAffiliateStats(stats);
      } else {
        setError(result.error || 'Něco se pokazilo');
      }
    } catch (err) {
      setError('Nepodařilo se aktivovat affiliate účet');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const copyAffiliateLink = () => {
    const link = `https://synapso.cz/?ref=${affiliateCode}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSavePayoutSettings = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await api.updatePayoutSettings(user.id, payoutSettings);
      if (result.success) {
        setMessage({ type: 'success', text: 'Platební údaje byly uloženy.' });
        setShowPayoutForm(false);
      } else {
        setMessage({ type: 'error', text: result.error || 'Nepodařilo se uložit.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Nepodařilo se uložit platební údaje.' });
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-dark-950 pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // AFFILIATE DASHBOARD - když je uživatel affiliate
  if (isAffiliate) {
    return (
      <div className="min-h-screen bg-dark-950 pt-24 pb-20">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-40 -right-40 w-80 h-80 bg-primary-500/20 rounded-full blur-3xl"></div>
          <div className="absolute top-60 -left-40 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/30 mb-4">
              <CheckCircle size={18} className="text-green-400" />
              <span className="text-green-400 font-medium">Aktivní affiliate</span>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Affiliate Dashboard</h1>
            <p className="text-dark-400">Získej 20% provizi z každého předplatného</p>
          </div>

          {/* Message */}
          {message.text && (
            <div className={`mb-6 p-4 rounded-xl flex items-center space-x-3 ${
              message.type === 'success' ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle size={20} className="text-green-400" />
              ) : (
                <AlertCircle size={20} className="text-red-400" />
              )}
              <span className={message.type === 'success' ? 'text-green-400' : 'text-red-400'}>
                {message.text}
              </span>
            </div>
          )}

          {/* Affiliate Link */}
          <div className="glass rounded-2xl p-6 mb-8">
            <h2 className="text-white font-semibold mb-4">Tvůj affiliate odkaz</h2>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <input
                type="text"
                value={`https://synapso.cz/?ref=${affiliateCode}`}
                readOnly
                className="flex-1 px-4 py-3 rounded-xl bg-dark-800 text-white border border-dark-700 text-sm"
              />
              <button
                onClick={copyAffiliateLink}
                className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center justify-center space-x-2 ${
                  copied ? 'bg-green-500 text-white' : 'bg-primary-500 text-white hover:bg-primary-600'
                }`}
              >
                {copied ? <Check size={20} /> : <Copy size={20} />}
                <span>{copied ? 'Zkopírováno!' : 'Kopírovat'}</span>
              </button>
            </div>
            <p className="text-dark-500 text-sm mt-3">
              Tvůj kód: <span className="text-primary-400 font-mono">{affiliateCode}</span>
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="glass rounded-xl p-5">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <MousePointer size={20} className="text-blue-400" />
                </div>
                <span className="text-dark-400 text-sm">Kliknutí</span>
              </div>
              <p className="text-3xl font-bold text-white">{affiliateStats?.clicks || 0}</p>
            </div>

            <div className="glass rounded-xl p-5">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <UserPlus size={20} className="text-purple-400" />
                </div>
                <span className="text-dark-400 text-sm">Registrací</span>
              </div>
              <p className="text-3xl font-bold text-white">{affiliateStats?.signups || 0}</p>
            </div>

            <div className="glass rounded-xl p-5">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <RefreshCw size={20} className="text-green-400" />
                </div>
                <span className="text-dark-400 text-sm">Konverzí</span>
              </div>
              <p className="text-3xl font-bold text-white">{affiliateStats?.conversions || 0}</p>
            </div>

            <div className="glass rounded-xl p-5">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <DollarSign size={20} className="text-amber-400" />
                </div>
                <span className="text-dark-400 text-sm">Vyděláno</span>
              </div>
              <p className="text-3xl font-bold text-white">
                {affiliateStats?.earned || 0} <span className="text-lg text-dark-400">Kč</span>
              </p>
            </div>
          </div>

          {/* Balance & Payout Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Balance */}
            <div className="glass rounded-2xl p-6">
              <h2 className="text-white font-semibold mb-4">Zůstatek</h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-dark-400">Čeká na schválení</span>
                  <span className="text-white font-semibold">{affiliateStats?.pendingBalance || 0} Kč</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-dark-400">K vyplacení</span>
                  <span className="text-green-400 font-semibold">{affiliateStats?.availableForPayout || 0} Kč</span>
                </div>
                <div className="border-t border-dark-700 pt-4">
                  <p className="text-dark-500 text-sm">Minimální výplata: {affiliateStats?.minimumPayout || 500} Kč</p>
                </div>
              </div>
            </div>

            {/* Payout Settings */}
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-semibold">Platební údaje</h2>
                <button
                  onClick={() => setShowPayoutForm(!showPayoutForm)}
                  className="p-2 rounded-lg bg-dark-800 hover:bg-dark-700 transition-colors"
                >
                  <Settings size={18} className="text-dark-400" />
                </button>
              </div>

              {!showPayoutForm ? (
                <div className="space-y-3">
                  {payoutSettings.payoutMethod ? (
                    <>
                      <div className="flex items-center space-x-3 p-3 rounded-xl bg-dark-800/50">
                        <CreditCard size={20} className="text-primary-400" />
                        <div>
                          <p className="text-white font-medium">
                            {payoutSettings.payoutMethod === 'paypal' ? 'PayPal' : 'Bankovní převod'}
                          </p>
                          <p className="text-dark-400 text-sm">
                            {payoutSettings.payoutMethod === 'paypal'
                              ? payoutSettings.paypalEmail
                              : payoutSettings.bankIban || payoutSettings.bankAccountNumber}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowPayoutForm(true)}
                        className="text-primary-400 text-sm hover:underline"
                      >
                        Změnit platební údaje
                      </button>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-dark-400 mb-3">Zatím nemáš nastavené platební údaje</p>
                      <button
                        onClick={() => setShowPayoutForm(true)}
                        className="px-4 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors"
                      >
                        Nastavit
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Výběr metody */}
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setPayoutSettings(prev => ({ ...prev, payoutMethod: 'paypal' }))}
                      className={`flex-1 py-3 rounded-xl border transition-colors ${
                        payoutSettings.payoutMethod === 'paypal'
                          ? 'border-primary-500 bg-primary-500/10 text-white'
                          : 'border-dark-700 bg-dark-800 text-dark-400 hover:border-dark-600'
                      }`}
                    >
                      PayPal
                    </button>
                    <button
                      onClick={() => setPayoutSettings(prev => ({ ...prev, payoutMethod: 'bank' }))}
                      className={`flex-1 py-3 rounded-xl border transition-colors ${
                        payoutSettings.payoutMethod === 'bank'
                          ? 'border-primary-500 bg-primary-500/10 text-white'
                          : 'border-dark-700 bg-dark-800 text-dark-400 hover:border-dark-600'
                      }`}
                    >
                      Banka
                    </button>
                  </div>

                  {payoutSettings.payoutMethod === 'paypal' && (
                    <div>
                      <label className="block text-dark-400 text-sm mb-2">PayPal email</label>
                      <input
                        type="email"
                        value={payoutSettings.paypalEmail}
                        onChange={(e) => setPayoutSettings(prev => ({ ...prev, paypalEmail: e.target.value }))}
                        placeholder="vas@email.cz"
                        className="w-full px-4 py-3 rounded-xl bg-dark-800 border border-dark-700 text-white placeholder-dark-500 focus:outline-none focus:border-primary-500"
                      />
                    </div>
                  )}

                  {payoutSettings.payoutMethod === 'bank' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-dark-400 text-sm mb-2">Jméno majitele účtu</label>
                        <input
                          type="text"
                          value={payoutSettings.bankAccountHolder}
                          onChange={(e) => setPayoutSettings(prev => ({ ...prev, bankAccountHolder: e.target.value }))}
                          placeholder="Jan Novák"
                          className="w-full px-4 py-3 rounded-xl bg-dark-800 border border-dark-700 text-white placeholder-dark-500 focus:outline-none focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-dark-400 text-sm mb-2">Číslo účtu</label>
                        <input
                          type="text"
                          value={payoutSettings.bankAccountNumber}
                          onChange={(e) => setPayoutSettings(prev => ({ ...prev, bankAccountNumber: e.target.value }))}
                          placeholder="123456789/0100"
                          className="w-full px-4 py-3 rounded-xl bg-dark-800 border border-dark-700 text-white placeholder-dark-500 focus:outline-none focus:border-primary-500"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex space-x-3 pt-2">
                    <button
                      onClick={() => setShowPayoutForm(false)}
                      className="flex-1 py-3 rounded-xl border border-dark-700 text-dark-400 hover:text-white hover:border-dark-600 transition-colors"
                    >
                      Zrušit
                    </button>
                    <button
                      onClick={handleSavePayoutSettings}
                      disabled={saving || !payoutSettings.payoutMethod}
                      className="flex-1 py-3 rounded-xl bg-primary-500 text-white hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                    >
                      {saving ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          <span>Ukládám...</span>
                        </>
                      ) : (
                        <span>Uložit</span>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Commission Structure */}
          <div className="glass rounded-2xl p-8">
            <h2 className="text-xl font-bold text-white mb-6">Struktura provizí</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 rounded-xl bg-dark-800/50">
                <p className="text-dark-400 mb-2">1 měsíc (249 Kč)</p>
                <p className="text-3xl font-bold text-primary-400">50 Kč</p>
                <p className="text-dark-500 text-sm mt-1">za každé předplatné</p>
              </div>
              <div className="text-center p-6 rounded-xl bg-dark-800/50 ring-2 ring-primary-500">
                <p className="text-dark-400 mb-2">3 měsíce (699 Kč)</p>
                <p className="text-3xl font-bold text-primary-400">140 Kč</p>
                <p className="text-dark-500 text-sm mt-1">za každé předplatné</p>
              </div>
              <div className="text-center p-6 rounded-xl bg-dark-800/50">
                <p className="text-dark-400 mb-2">6 měsíců (1290 Kč)</p>
                <p className="text-3xl font-bold text-primary-400">258 Kč</p>
                <p className="text-dark-500 text-sm mt-1">za každé předplatné</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // LANDING PAGE - když uživatel NENÍ affiliate
  const benefits = [
    {
      icon: DollarSign,
      title: '20% provize',
      description: 'Získej 20% z každé platby uživatelů, které přivedeš'
    },
    {
      icon: TrendingUp,
      title: 'Opakované příjmy',
      description: 'Provize dostáváš z každé platby'
    },
    {
      icon: BarChart3,
      title: 'Přehledný dashboard',
      description: 'Sleduj své statistiky a výdělky v reálném čase'
    },
    {
      icon: Zap,
      title: 'Rychlé výplaty',
      description: 'Výplaty na PayPal nebo bankovní účet'
    }
  ];

  const steps = [
    { number: '1', title: 'Aktivuj účet', description: 'Klikni na tlačítko a staň se affiliate' },
    { number: '2', title: 'Získej odkaz', description: 'Dostaneš unikátní affiliate odkaz' },
    { number: '3', title: 'Sdílej a vydělávej', description: 'Získávej provizi z každého předplatného' }
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-24 pb-20">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-40 -right-40 w-80 h-80 bg-primary-500/20 rounded-full blur-3xl"></div>
        <div className="absolute top-60 -left-40 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/30 mb-6">
            <Share2 size={18} className="text-primary-400" />
            <span className="text-primary-400 font-medium">Affiliate program</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Vydělávej s námi</h1>
          <p className="text-dark-400 max-w-2xl mx-auto text-lg">
            Připoj se k našemu affiliate programu a získej 20% provizi za každého nového uživatele.
          </p>
        </div>

        {/* CTA - hned nahoře */}
        <div className="max-w-md mx-auto mb-16">
          <div className="glass rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-primary-500 to-purple-600 flex items-center justify-center mx-auto mb-4">
              <Gift size={32} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              {user ? 'Staň se affiliate' : 'Přihlas se do programu'}
            </h2>
            <p className="text-dark-400 mb-6">
              {user
                ? 'Aktivuj si affiliate účet a začni vydělávat.'
                : 'Přihlas se a aktivuj si affiliate účet.'}
            </p>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}

            <button
              onClick={handleBecomeAffiliate}
              disabled={loading}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-primary-500 to-purple-600 text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>Aktivuji...</span>
                </>
              ) : (
                <span>{user ? 'Aktivovat affiliate účet' : 'Přihlásit se a aktivovat'}</span>
              )}
            </button>
            <p className="text-dark-500 text-xs mt-4">
              Aktivace je zdarma. Provizi dostaneš za každého platícího uživatele.
            </p>
          </div>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {benefits.map((benefit, index) => (
            <div key={index} className="glass rounded-2xl p-6 text-center">
              <div className="w-14 h-14 rounded-xl bg-primary-500/20 flex items-center justify-center mx-auto mb-4">
                <benefit.icon size={28} className="text-primary-400" />
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">{benefit.title}</h3>
              <p className="text-dark-400 text-sm">{benefit.description}</p>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-10">Jak to funguje?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="glass rounded-2xl p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary-500 to-purple-600 flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">{step.number}</span>
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{step.title}</h3>
                  <p className="text-dark-400 text-sm">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight size={24} className="text-dark-600" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Commission Structure */}
        <div className="glass rounded-2xl p-8 mb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Struktura provizí</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 rounded-xl bg-dark-800/50">
              <p className="text-dark-400 mb-2">1 měsíc (249 Kč)</p>
              <p className="text-3xl font-bold text-primary-400">50 Kč</p>
              <p className="text-dark-500 text-sm mt-1">za každé předplatné</p>
            </div>
            <div className="text-center p-6 rounded-xl bg-dark-800/50 ring-2 ring-primary-500">
              <p className="text-dark-400 mb-2">3 měsíce (699 Kč)</p>
              <p className="text-3xl font-bold text-primary-400">140 Kč</p>
              <p className="text-dark-500 text-sm mt-1">za každé předplatné</p>
            </div>
            <div className="text-center p-6 rounded-xl bg-dark-800/50">
              <p className="text-dark-400 mb-2">6 měsíců (1290 Kč)</p>
              <p className="text-3xl font-bold text-primary-400">258 Kč</p>
              <p className="text-dark-500 text-sm mt-1">za každé předplatné</p>
            </div>
          </div>
        </div>

        {/* Target Audience */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Pro koho je program vhodný?</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {['Studijní poradci', 'Influenceři', 'Učitelé', 'Vzdělávací weby', 'Rodiče studentů', 'Studijní skupiny'].map((item, index) => (
              <div key={index} className="flex items-center space-x-3 glass rounded-xl p-4">
                <CheckCircle size={20} className="text-green-400 flex-shrink-0" />
                <span className="text-dark-300 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Affiliate;
